import {
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { arrayMove, SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableColumn, EditorColumn } from "src/components/content-builder";
import { v4 as uuidv4 } from "uuid";
import DeleteIcon from "@mui/icons-material/Delete";

const GRID_OPTIONS = {
  "12": [12],
  "6/6": [6, 6],
  "4/4/4": [4, 4, 4],
  "3/3/3/3": [3, 3, 3, 3]
};

interface EditorRowProps {
  row: any;
  onUpdate: (updatedRow: any) => void;
  onRemove: (row: any) => void;
}

export const EditorRow: React.FC<EditorRowProps> = (props) => {
  const { row, onUpdate, onRemove } = props;

  const [grid, setGrid] = useState(row.grid);
  const [columns, setColumns] = useState(row.columns);

  useEffect(() => {
    if (grid.length !== columns.length) {
      const newCols = grid.map(() => ({ id: uuidv4(), component: null }));
      setColumns(newCols);
      onUpdate({ ...row, grid, columns: newCols });
    }
  }, [grid]);

  useEffect(() => {
    setColumns(row.columns);
  }, [row.columns]);

  const handleGridChange = (e) => {
    e.stopPropagation()
    e.preventDefault();

    const selected = e.target.value;
    const layout = GRID_OPTIONS[selected];
    setGrid(layout);
  };

  const updateColumn = (colId, updatedCol) => {
    const updatedColumns = columns.map(c => (c.id === colId ? updatedCol : c));
    setColumns(updatedColumns);
    onUpdate({ ...row, columns: updatedColumns });
  };

  const handleRemove = () => {
    onRemove(row);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = columns.findIndex(c => c.id === active.id);
      const newIndex = columns.findIndex(c => c.id === over.id);

      const newColumns = arrayMove(columns, oldIndex, newIndex);
      setColumns(newColumns);
      onUpdate({ ...row, columns: newColumns });
    }
  };

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <Box className="editor-row-container" sx={{ backgroundColor: 'white' }}>
        <Box className="editor-row-grid-select" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FormControl fullWidth={false}>
              <InputLabel>Grid</InputLabel>
              <Select
                value={Object.keys(GRID_OPTIONS).find(k => JSON.stringify(GRID_OPTIONS[k]) === JSON.stringify(grid)) || "12"}
                onChange={handleGridChange}
                className="editor-row-select"
                size="small"
                label="Grid"
              >
                {Object.entries(GRID_OPTIONS).map(([label]) => (
                  <MenuItem key={label} value={label}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton onClick={handleRemove}><DeleteIcon /></IconButton>
        </Box>

        <SortableContext items={columns.map(c => c.id)} strategy={horizontalListSortingStrategy}>
          <Box className="editor-row-columns" sx={{ display: 'flex', gap: 2 }}>
            {columns.map((column, index) => (
              <SortableColumn
                key={column.id}
                id={column.id}
                size={grid[index]}
              >
                <EditorColumn
                  column={column}
                  onUpdate={(updatedCol) => updateColumn(column.id, updatedCol)}
                />
              </SortableColumn>
            ))}
          </Box>
        </SortableContext>
      </Box>
    </DndContext>
  );
};
